// Input handling for keyboard and mouse
import CONFIG from './config.js';

export const Input = {
    keys: {},
    mouseX: 0,
    mouseY: 0,
    mouseWorldX: 0,
    mouseWorldY: 0,
    leftClicked: false,
    rightClicked: false,
    doubleClicked: false,
    lastClickTime: -999,
    lastKeyPressTime: {},
    doubleKeyPressed: null,
    keysUsedForDash: {}, // Track which keys have already triggered a dash (to prevent repeated dashes while holding)
    camera: null,

    init(canvas, camera) {
        this.camera = camera;

        window.addEventListener('keydown', (e) => {
            const key = e.key.toLowerCase();

            // Only process on first press, not while holding (e.repeat is true when holding)
            if (!e.repeat) {
                // Detect double-press for movement keys
                const movementKeys = ['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'];
                if (movementKeys.includes(key)) {
                    const now = performance.now() / 1000;
                    const lastPress = this.lastKeyPressTime[key] || -999;

                    // Only detect double-press if this key hasn't already been used for a dash
                    // This prevents repeated dashes while holding a key
                    if (!this.keysUsedForDash[key] && now - lastPress < CONFIG.doubleClickThreshold) {
                        // Double-press detected!
                        this.doubleKeyPressed = key;
                        this.keysUsedForDash[key] = true; // Mark this key as used for dash
                    }

                    this.lastKeyPressTime[key] = now;
                }
            }

            this.keys[key] = true;

            // Prevent arrow keys and space from scrolling
            if(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(e.key)) {
                e.preventDefault();
            }
        });

        window.addEventListener('keyup', (e) => {
            const key = e.key.toLowerCase();
            this.keys[key] = false;
            // Clear the "used for dash" flag when key is released
            // This allows the key to trigger a dash again on the next press
            delete this.keysUsedForDash[key];
        });

        // Track mouse position
        canvas.addEventListener('mousemove', (e) => {
            const rect = canvas.getBoundingClientRect();
            this.mouseX = e.clientX - rect.left;
            this.mouseY = e.clientY - rect.top;
            this.updateWorldCoordinates();
        });

        // Left click - movement (with double-click detection for dash)
        canvas.addEventListener('click', (e) => {
            if (e.button === 0) { // Left button
                e.preventDefault();
                const rect = canvas.getBoundingClientRect();
                this.mouseX = e.clientX - rect.left;
                this.mouseY = e.clientY - rect.top;
                this.updateWorldCoordinates();

                // Check for double-click
                const now = performance.now() / 1000;
                const timeSinceLastClick = now - this.lastClickTime;

                if (timeSinceLastClick < CONFIG.doubleClickThreshold) {
                    // Double-click detected!
                    this.doubleClicked = true;
                    this.leftClicked = false; // Don't register as single click
                } else {
                    this.leftClicked = true;
                }

                this.lastClickTime = now;
            }
        });

        // Right click - interaction
        canvas.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            const rect = canvas.getBoundingClientRect();
            this.mouseX = e.clientX - rect.left;
            this.mouseY = e.clientY - rect.top;
            this.updateWorldCoordinates();
            this.rightClicked = true;
        });
    },

    updateWorldCoordinates() {
        if (this.camera) {
            this.mouseWorldX = this.camera.x + this.mouseX;
            this.mouseWorldY = this.camera.y + this.mouseY;
        }
    },

    setCamera(camera) {
        this.camera = camera;
    },

    isDown(key) {
        return this.keys[key] || false;
    },

    consumeLeftClick() {
        const clicked = this.leftClicked;
        this.leftClicked = false;
        return clicked;
    },

    consumeRightClick() {
        const clicked = this.rightClicked;
        this.rightClicked = false;
        return clicked;
    },

    consumeDoubleClick() {
        const clicked = this.doubleClicked;
        this.doubleClicked = false;
        return clicked;
    },

    consumeDoubleKeyPress() {
        const key = this.doubleKeyPressed;
        this.doubleKeyPressed = null;
        return key;
    }
};
